/**
 * Desktop — renders all open windows + Cmd+K CommandPalette
 */
import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import { CommandPalette } from '../CommandPalette'
import DesktopHero from '../DesktopHero/DesktopHero'
import LockScreen from '../LockScreen/LockScreen'
import WelcomeToast from '../Notifications/WelcomeToast'
import OSWindow from './OSWindow'
import { useWindowManager } from './WindowManagerContext'
import MissionControl from '../MissionControl/MissionControl'

export default function Desktop() {
  const { windows, minimizeAll, restoreAll } = useWindowManager()
  const { user } = useAuth()
  const [cmdkOpen, setCmdkOpen] = useState(false)
  const [missionControlOpen, setMissionControlOpen] = useState(false)
  const [lockScreenOpen, setLockScreenOpen] = useState(false)
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null)
  const [showDesktopToast, setShowDesktopToast] = useState(false)
  const showDesktopRef = useRef(false)
  const [showOnboardingBadge, setShowOnboardingBadge] = useState(false)

  // OB-1: Onboarding — artık zorla açılmıyor, dismissible badge gösterilir
  // ESKİ KOD:
  // useEffect(() => { if (user?.onboarding_status === 'pending') setTimeout(() => dispatch('runai:open-window','user-hub'), 1000) }, [user])
  useEffect(() => {
    if (!user) return
    if ((user as any).onboarding_status !== 'pending') return
    if (localStorage.getItem('runai_onboarding_dismissed')) return
    const t = setTimeout(() => setShowOnboardingBadge(true), 1000)
    const autoHide = setTimeout(() => setShowOnboardingBadge(false), 11000)
    return () => { clearTimeout(t); clearTimeout(autoHide) }
  }, [user])

  // Cmd+K / Ctrl+K → CommandPalette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCmdkOpen(prev => !prev)
      }
      if (e.metaKey && e.ctrlKey && e.key === 'q') {
        e.preventDefault()
        setLockScreenOpen(true)
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === '/' || e.key === '?')) {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'keyboard-shortcuts' }))
      }
      if (e.key === 'Escape') { setCmdkOpen(false); setCtxMenu(null); setMissionControlOpen(false) }
      if (e.key === 'F3' || (e.metaKey && e.key === 'ArrowUp')) {
        e.preventDefault(); setMissionControlOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // F11 → Show Desktop (minimize all / restore all toggle)
  useEffect(() => {
    const lockHandler = () => setLockScreenOpen(true)
    window.addEventListener('runai:lock-screen', lockHandler)
    return () => window.removeEventListener('runai:lock-screen', lockHandler)
  }, [])

  // F11 → Show Desktop (minimize all / restore all toggle - original)
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'F11') {
        e.preventDefault()
        const allMinimized = windows.length > 0 && windows.every(w => w.minimized)
        if (allMinimized || showDesktopRef.current) {
          restoreAll()
          showDesktopRef.current = false
        } else {
          minimizeAll()
          showDesktopRef.current = true
        }
        setShowDesktopToast(true)
        setTimeout(() => setShowDesktopToast(false), 1000)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [windows, minimizeAll, restoreAll])

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault()
    setCtxMenu({ x: e.clientX, y: e.clientY })
  }

  return (
    <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0 }} onContextMenu={handleContextMenu} onClick={() => setCtxMenu(null)}>
      {/* NeuralScreensaver disabled — causes Mac freeze on 8GB RAM */}
      {/* <NeuralScreensaver visible={!hasOpenWindows} /> */}
      <DesktopHero />
      {windows.map(win => (
        <OSWindow key={win.id} win={win} />
      ))}
      {cmdkOpen && <CommandPalette isOpen={cmdkOpen} onClose={() => setCmdkOpen(false)} />}
      {missionControlOpen && <MissionControl onClose={() => setMissionControlOpen(false)} />}
      {lockScreenOpen && <LockScreen onUnlock={() => setLockScreenOpen(false)} />}
      <WelcomeToast />

      {/* OB-1: Dismissible onboarding badge */}
      {showOnboardingBadge && (
        <div style={{ position: 'fixed', top: 12, right: 12, zIndex: 9999, display: 'flex', alignItems: 'center', gap: 6, background: 'linear-gradient(135deg,#8b5cf6,#3b82f6)', color: '#fff', padding: '8px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 16px rgba(139,92,246,0.4)' }}
          onClick={() => { setShowOnboardingBadge(false); window.dispatchEvent(new CustomEvent('runai:open-window', { detail: 'user-hub' })) }}>
          🚀 Onboarding
          <button onClick={e => { e.stopPropagation(); setShowOnboardingBadge(false); localStorage.setItem('runai_onboarding_dismissed', 'true') }}
            style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}>
            <X size={10} color="#fff" />
          </button>
        </div>
      )}

      {/* Show Desktop Toast */}
      {showDesktopToast && (
        <div style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          background: 'rgba(0,0,0,0.75)', color: '#fff', padding: '12px 24px',
          borderRadius: 12, fontSize: 14, fontWeight: 500, zIndex: 99999,
          backdropFilter: 'blur(8px)', pointerEvents: 'none',
        }}>Show Desktop</div>
      )}

      {/* Desktop Context Menu */}
      {ctxMenu && (
        <div onClick={e => e.stopPropagation()} style={{
          position: 'fixed', left: ctxMenu.x, top: ctxMenu.y, zIndex: 9998,
          background: 'var(--os-surface)', backdropFilter: 'blur(20px)',
          border: '1px solid var(--os-border)', borderRadius: 8,
          boxShadow: 'var(--os-card-shadow)', padding: '4px 0', minWidth: 180,
        }}>
          {[
            { label: 'Command Palette', shortcut: '⌘K', action: () => { setCtxMenu(null); setCmdkOpen(true) } },
            { label: 'Change Wallpaper', action: () => { setCtxMenu(null); /* open settings */ } },
            { label: 'Toggle Theme', action: () => { setCtxMenu(null); document.querySelector<HTMLButtonElement>('[data-theme-toggle]')?.click() } },
            { label: 'Refresh Desktop', action: () => { setCtxMenu(null); window.location.reload() } },
          ].map((item, i) => (
            <button key={i} onClick={item.action} style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '7px 14px', border: 'none', background: 'transparent',
              color: 'var(--os-text)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
            }}
              onMouseEnter={e => { (e.target as HTMLElement).style.background = 'var(--os-hover)' }}
              onMouseLeave={e => { (e.target as HTMLElement).style.background = 'transparent' }}
            >
              <span>{item.label}</span>
              {'shortcut' in item && <span style={{ fontSize: 10, color: 'var(--os-muted)', fontFamily: 'monospace' }}>{item.shortcut}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
